import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCookie, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

type CookieConsentType = 'all' | 'necessary' | null;

const CookieConsent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent') as CookieConsentType;
    if (!consent) {
      setShowBanner(true);
    }

    // Check if current language is RTL
    setIsRTL(i18n.language === 'ar');
  }, [i18n.language]);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    setShowBanner(false);
    // Here you can enable all tracking scripts (Microsoft Clarity, etc.)
    enableTrackingScripts();
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('cookieConsent', 'necessary');
    setShowBanner(false);
    // Only enable necessary cookies, disable tracking scripts
    disableTrackingScripts();
  };

  const enableTrackingScripts = () => {
    // Enable Microsoft Clarity and other tracking scripts
    // This would typically be done by loading the scripts dynamically
    if ((window as any).clarity) {
      // Clarity is already loaded
      return;
    }
    // Note: Microsoft Clarity script should be loaded in index.html
    // This function is for future use if you need to enable/disable dynamically
  };

  const disableTrackingScripts = () => {
    // Disable tracking scripts
    // Note: This is a placeholder - actual implementation depends on how scripts are loaded
    // You may need to remove or block the scripts
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 ${isRTL ? 'rtl' : ''}`}
      style={{
        background: 'var(--gp-surface)',
        borderTop: '1px solid var(--gp-border)',
        boxShadow: 'var(--gp-shadow-lg)',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="gp-container py-3 sm:py-3.5">
        <div className={`flex flex-col ${isRTL ? 'md:flex-row-reverse' : 'md:flex-row'} items-start md:items-center justify-between gap-3`}>
          {/* Cookie Icon and Message */}
          <div className={`flex items-start gap-2.5 ${isRTL ? 'flex-row-reverse' : ''} flex-1`}>
            <div
              className="gp-icon-tile flex-shrink-0 mt-0.5"
              aria-hidden="true"
            >
              <FontAwesomeIcon icon={faCookie} className="text-sm" />
            </div>
            <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h3 className="text-sm font-semibold mb-0.5" style={{ color: 'var(--gp-ink)' }}>
                {t('cookieConsent.title', 'We use cookies')}
              </h3>
              <p className="text-xs leading-relaxed gp-text-muted">
                {t(
                  'cookieConsent.message',
                  'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies. You can also choose to accept only necessary cookies.'
                )}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''} flex-shrink-0`}>
            <button
              onClick={handleAcceptNecessary}
              className={`gp-btn-ghost text-xs py-1.5 px-3 ${isRTL ? 'order-2' : ''}`}
            >
              {t('cookieConsent.acceptNecessary', 'Necessary Only')}
            </button>
            <button
              onClick={handleAcceptAll}
              className={`gp-btn-primary text-xs py-1.5 px-3 ${isRTL ? 'order-1' : ''}`}
            >
              <FontAwesomeIcon icon={faCheckCircle} className="text-xs" />
              {t('cookieConsent.acceptAll', 'Accept All')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
